import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  MenuItem,
  Stack,
  Avatar,
  IconButton,
  useTheme,
  alpha,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  HelpOutline as HelpIcon,
  Logout as LogoutIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useProjectContext } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import NotificationPanel from '../components/NotificationPanel';
import NotificationToast from '../components/NotificationToast';
import ProjectSelector from '../components/ProjectSelector';
import HelpDrawer from '../components/help/HelpDrawer';
import { LayoutFrameProvider, useLayoutFrame } from '../context/LayoutFrameContext';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayoutInner: React.FC<MainLayoutProps> = ({ children }) => {
  const { appBarHidden } = useLayoutFrame();
  const theme = useTheme();
  const { projects, selectedProjectId, setSelectedProjectId, isLoadingProjects, refreshSummary } = useProjectContext();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    void logout();
  };

  // Get user initials
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Batch routes use their own layout — remove outer padding so canvas is full-bleed
  const isBatchRoute = location.pathname.startsWith('/batches/') && location.pathname.split('/').length > 2;

  const navItems = [
    { label: 'Home', path: '/dashboard' },
    { label: 'Transformations', path: '/batches' },
    { label: 'Projects', path: '/projects' },
    { label: 'Formats', path: '/schema-sets' },
    // Rule Library Hidden
    { label: 'Connections', path: '/connectors' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <CssBaseline />

      {/* Top Header */}
      {!appBarHidden && (
      <AppBar
        position="static"
        color="transparent"
        elevation={0}
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(8px)',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          {/* Logo */}
          <Box
            component="img"
            src="/images/migratr.ai L.png"
            alt="migratr"
            sx={{
              height: 24,
              width: 'auto',
              mr: 4,
              cursor: 'pointer'
            }}
            onClick={() => navigate('/dashboard')}
          />

          {/* Navigation Links */}
          <Stack direction="row" spacing={1} sx={{ flexGrow: 1 }}>
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
              return (
                <Box
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    if (item.path === '/batches') {
                      refreshSummary();
                    }
                  }}
                  sx={{
                    px: 2,
                    py: 1,
                    cursor: 'pointer',
                    borderRadius: 1,
                    color: isActive ? 'primary.main' : 'text.secondary',
                    bgcolor: isActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    fontWeight: isActive ? 600 : 500,
                    fontSize: '0.875rem',
                    '&:hover': {
                      bgcolor: isActive ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.text.primary, 0.05),
                      color: isActive ? 'primary.main' : 'text.primary',
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  {item.label}
                </Box>
              );
            })}
          </Stack>


          {/* Right Side Actions */}
          <Stack direction="row" spacing={2} alignItems="center">
            {/* Project Selector */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                component="span"
                sx={{
                  whiteSpace: 'nowrap',
                  color: 'text.secondary',
                  fontFamily: theme.typography.fontFamily,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                Current project:
              </Box>
              <ProjectSelector
                projects={projects}
                selectedProjectId={selectedProjectId}
                onSelectProject={setSelectedProjectId}
                isLoading={isLoadingProjects}
                minWidth={200}
              />
            </Stack>

            <Tooltip
              title="Open help"
              placement="bottom"
              arrow
              componentsProps={{
                popper: {
                  sx: { zIndex: (t) => t.zIndex.tooltip },
                },
              }}
            >
              <IconButton size="small" color="inherit" onClick={() => setHelpOpen(true)} aria-label="Open help">
                <HelpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
            <NotificationPanel />

            {/* User Avatar with Menu */}
            <IconButton onClick={handleAvatarClick} size="small">
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: theme.palette.primary.main,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {user ? getInitials(`${user.first_name} ${user.last_name ?? ''}`) : 'U'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: { minWidth: 200, mt: 1 }
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {user ? `${user.first_name} ${user.last_name ?? ''}`.trim() : 'User'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email || ''}
                </Typography>
              </Box>
              <Divider />
              {user?.role === 'admin' && (
                <MenuItem onClick={() => { handleMenuClose(); navigate('/admin'); }}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Admin Dashboard</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={() => { handleMenuClose(); navigate('/profile'); }}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Profile</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>Sign Out</ListItemText>
              </MenuItem>
            </Menu>
          </Stack>
        </Toolbar>
      </AppBar>
      )}

      {/* Content Scroll Area — batch routes use full bleed, others get padding */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          overflow: isBatchRoute ? 'hidden' : 'auto',
          p: isBatchRoute ? 0 : 3,
          bgcolor: 'background.default',
          width: '100%',
          display: isBatchRoute ? 'flex' : 'block',
          flexDirection: isBatchRoute ? 'column' : undefined,
          minHeight: 0,
        }}
      >
        {children}
      </Box>
      <NotificationToast />
    </Box>
  );
};

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => (
  <LayoutFrameProvider>
    <MainLayoutInner>{children}</MainLayoutInner>
  </LayoutFrameProvider>
);

export default MainLayout;